import React, { forwardRef, ForwardRefRenderFunction, ReactNode, useMemo } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';

import { BaseStyleEnums, CoreUtils, ReactTable, TimeZoneEnums } from '@nhl/nhl-breakaway-ui';
import { ColumnDef } from '@tanstack/react-table';

import commonMessages from '~/core/commonMessages';
import { DAY_MONTH_DATE } from '~/core/dateTimeFormats';
import { PageName } from '~/core/enums';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { defaultLocationData, useLocationQuery } from '~/redux/ducks/location';

import { OddDetails, ScheduleGame, ScheduleGameDay } from '../../../../types';
import Promo from '../promo/Promo';

import { liveGameColumns, postGameColumns, preGameColumns } from './columns';
import { getMinimumGameState, multipleGameStatesCheck } from './utils';

import { DateHeader, DateHeaderContainer, HeaderContainer, NoGamesText, ReactTableContainer } from './styles';

type Props = {
    day: ScheduleGameDay;
    hideBettingOdds: boolean;
    oddDetails: OddDetails;
    pageName: PageName;
    timezone: TimeZoneEnums;
    teamTriCode?: string;
    isFullSeason: boolean;
};

const DayOverviewRenderFunc: ForwardRefRenderFunction<HTMLDivElement, Props> = (
    { day, hideBettingOdds, oddDetails, pageName, timezone, teamTriCode, isFullSeason },
    dayRef
) => {
    const intl = useIntl();
    const size = useSizeWithThrottle();
    const isSmall = size.width <= BaseStyleEnums.ScreenWidthBreakpoints.small;
    const isLessThanMedium = size.width <= BaseStyleEnums.ScreenWidthBreakpoints.medium;
    const isLarge = size.width >= BaseStyleEnums.ScreenWidthBreakpoints.large;

    const { data: countryCode = defaultLocationData } = useLocationQuery({});

    const dayGameState = getMinimumGameState(day.games);
    const multipleGameStates = multipleGameStatesCheck(day.games);

    const singleGameStateColumns = useMemo<ColumnDef<ScheduleGame>[]>(() => {
        if (CoreUtils.isPostGame(dayGameState)) {
            return postGameColumns(oddDetails.bettingOddPartner, intl, countryCode, pageName, isLarge);
        } else if (CoreUtils.isGameLive(dayGameState)) {
            return liveGameColumns(oddDetails.bettingOddPartner, intl, countryCode, pageName, teamTriCode);
        }
        return preGameColumns(
            oddDetails.bettingOddPartner,
            intl,
            countryCode,
            pageName,
            teamTriCode !== undefined,
            teamTriCode
        );
    }, [countryCode, dayGameState, intl, isLarge, oddDetails.bettingOddPartner, pageName, teamTriCode]);

    const gameDate = DateTime.fromISO(day.date);
    const dateDescription = gameDate.toLocaleString(DAY_MONTH_DATE);

    const createScheduleTable = (
        games: Array<ScheduleGame>,
        columns: Array<ColumnDef<ScheduleGame>>
    ): ReactNode | null =>
        games.length > 0 ? (
            <ReactTable
                columns={columns}
                data={games}
                meta={{
                    timezone,
                    isSmall,
                    showBettingOdds: !hideBettingOdds && oddDetails.daysWithOdds[day.date],
                }}
            />
        ) : null;

    return (
        <>
            <DateHeaderContainer ref={dayRef} $isFullSeason={isFullSeason}>
                <HeaderContainer>
                    <DateHeader
                        id={gameDate.toISODate() || undefined}
                        aria-level={2}
                        $isLessThanMedium={isLessThanMedium}
                        $isLarge={isLarge}
                    >
                        {dateDescription}
                    </DateHeader>
                    <Promo promos={day.datePromo} />
                </HeaderContainer>
            </DateHeaderContainer>
            {day.games.length === 0 ? (
                <NoGamesText>
                    <FormattedMessage {...commonMessages.misc.noGamesScheduled} />
                </NoGamesText>
            ) : (
                <ReactTableContainer $multipleGameStates={multipleGameStates}>
                    {multipleGameStates ? (
                        <>
                            {createScheduleTable(
                                day.games.filter((game) => CoreUtils.isGameLive(game.gameState)),
                                liveGameColumns(oddDetails.bettingOddPartner, intl, countryCode, pageName, teamTriCode)
                            )}
                            {createScheduleTable(
                                day.games.filter((game) => CoreUtils.isPregame(game.gameState)),
                                preGameColumns(
                                    oddDetails.bettingOddPartner,
                                    intl,
                                    countryCode,
                                    pageName,
                                    teamTriCode !== undefined,
                                    teamTriCode
                                )
                            )}
                            {createScheduleTable(
                                day.games.filter((game) => CoreUtils.isPostGame(game.gameState)),
                                postGameColumns(oddDetails.bettingOddPartner, intl, countryCode, pageName, isLarge)
                            )}
                        </>
                    ) : (
                        createScheduleTable(day.games, singleGameStateColumns)
                    )}
                </ReactTableContainer>
            )}
        </>
    );
};

const DayOverview = forwardRef<HTMLElement, Props>(DayOverviewRenderFunc);

export default DayOverview;
